import { OBSERVATORY } from '@/constants/observatory'

export const TIME_ZONE = OBSERVATORY.timeZone

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
})

export type ZonedParts = {
    year: number
    month: number
    day: number
    hour: number
    minute: number
    second: number
}

export const zonedParts = (date: Date): ZonedParts => {
    const parts = partsFormatter.formatToParts(date)
    const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value ?? 0)

    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour') % 24,
        minute: read('minute'),
        second: read('second')
    }
}

const offsetMinutes = (date: Date): number => {
    const p = zonedParts(date)
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)

    return Math.round((asUtc - date.getTime()) / 60000)
}

/** The instant at which the observatory's clocks read the given wall time. */
export const fromLocal = (year: number, month: number, day: number, hour = 0, minute = 0): Date => {
    const guess = Date.UTC(year, month - 1, day, hour, minute)
    const offset = offsetMinutes(new Date(guess))
    let result = guess - offset * 60000
    const check = offsetMinutes(new Date(result))

    if (check !== offset) result = guess - check * 60000

    return new Date(result)
}

const pad = (value: number) => String(value).padStart(2, '0')

/** YYYY-MM-DD as the observatory's calendar reads it. */
export const dateKey = (date: Date): string => {
    const p = zonedParts(date)

    return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

export const parseDateKey = (key: string): { year: number, month: number, day: number } => {
    const [year, month, day] = key.split('-').map(Number)

    return { year, month, day }
}

export const isDateKey = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)

export const addDays = (key: string, days: number): string => {
    const { year, month, day } = parseDateKey(key)
    const next = new Date(Date.UTC(year, month - 1, day + days))

    return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`
}

/** Day of week for a date key, 0 Sunday to 6 Saturday. */
export const weekdayOf = (key: string): number => {
    const { year, month, day } = parseDateKey(key)

    return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

/** Local noon on the given calendar day, a safe starting point for searching a night's events. */
export const localNoon = (key: string): Date => {
    const { year, month, day } = parseDateKey(key)

    return fromLocal(year, month, day, 12, 0)
}

/** The calendar day an evening belongs to: before 06:00 counts as the previous night. */
export const nightKeyFor = (date: Date): string => {
    const p = zonedParts(date)
    const key = `${p.year}-${pad(p.month)}-${pad(p.day)}`

    return p.hour < 6 ? addDays(key, -1) : key
}

const timeFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
const dayFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, weekday: 'short', day: 'numeric', month: 'short' })
const longDayFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long' })
const fullFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

export const formatTime = (date: Date | null | undefined): string => (date ? timeFormatter.format(date) : '--:--')

export const formatDay = (date: Date): string => dayFormatter.format(date)

export const formatLongDay = (date: Date): string => longDayFormatter.format(date)

export const formatFullDate = (date: Date): string => fullFormatter.format(date)

/** Formats a date key without any time-zone drift. */
export const formatKey = (key: string, style: 'short' | 'long' | 'full' = 'short'): string => {
    const { year, month, day } = parseDateKey(key)
    const date = new Date(Date.UTC(year, month - 1, day, 12))
    const options: Intl.DateTimeFormatOptions = style === 'short'
        ? { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }
        : style === 'long'
            ? { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }
            : { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }

    return new Intl.DateTimeFormat('en-GB', options).format(date)
}

export const formatDuration = (minutes: number): string => {
    const h = Math.floor(minutes / 60)
    const m = Math.round(minutes % 60)

    if (h === 0) return `${m} min`
    if (m === 0) return `${h} h`

    return `${h} h ${m} min`
}
